// Privacy operations: export, deletion and retention of a customer's data (CHT-771, Milestone E Phase 13.5;
// docs/engineering/privacy-operations.md). Pure: no Postgres, no provider.
//
// A privacy request is one row with a closed state machine. A DELETE request runs as an ordered list of STEPS. Every step is idempotent
// and is recorded as it finishes, so a failed provider step is retried on its own and a finished step is never done twice. The
// retention matrix at the bottom is the single list of what is deleted, anonymized or kept, and why. The admin confirm dialog, the
// API and the document read it, and a test fails when the document and this list differ.
export const privacyRequestKinds = Object.freeze(["EXPORT", "DELETE"]);
/**
 * `REQUESTED`: waits for a supervisor or an administrator. `APPROVED`: the durable job is stored. `EXECUTING`: a run has started the steps.
 * `COMPLETED`: every step is done. `PARTIALLY_COMPLETED`: the job used its attempts and at least one step is still `FAILED`; it stays visible and
 * an operator re-run resumes it. `REJECTED` and `CANCELLED` are final and change no customer data.
 */
export const privacyRequestStates = Object.freeze([
    "REQUESTED",
    "APPROVED",
    "EXECUTING",
    "COMPLETED",
    "PARTIALLY_COMPLETED",
    "REJECTED",
    "CANCELLED",
]);
export const privacyRequesterKinds = Object.freeze(["customer", "staff"]);
/** Why the request exists. A closed list: a free text reason would be personal data in a table that outlives the customer. */
export const privacyReasonCodes = Object.freeze([
    "CUSTOMER_REQUEST",
    "STAFF_ASSISTED",
    "LEGAL_REQUEST",
    "TEST_CUSTOMER",
]);
export const privacyStepStates = Object.freeze(["PENDING", "DONE", "FAILED"]);
const transitions = Object.freeze({
    REQUESTED: Object.freeze(["APPROVED", "REJECTED", "CANCELLED"]),
    APPROVED: Object.freeze(["EXECUTING", "CANCELLED"]),
    EXECUTING: Object.freeze(["COMPLETED", "PARTIALLY_COMPLETED"]),
    PARTIALLY_COMPLETED: Object.freeze(["EXECUTING"]),
    COMPLETED: Object.freeze([]),
    REJECTED: Object.freeze([]),
    CANCELLED: Object.freeze([]),
});
export const privacyRequestTransitions = transitions;
export function canTransitionPrivacyRequest(from, to) {
    return transitions[from].includes(to);
}
export const isFinalPrivacyState = (state) => transitions[state].length === 0;
// --- steps ------------------------------------------------------------------------------------------------------------------------------
/**
 * The steps of a DELETE request, in the order they run. `database` steps change Postgres through the one erasure function
 * (`lafayette_privacy_erase_step`). `provider` steps call a vendor and are retried one by one. `SUSPEND` comes first and stops all processing of
 * the customer at once; `TOMBSTONE` comes last and ends the link to the sign-in account. A provider step reads the identifiers it needs from the
 * cleanup manifest that `SUSPEND` wrote, so it still works after the database steps removed those identifiers.
 */
export const privacyDeleteSteps = Object.freeze([
    { step: "SUSPEND", phase: "database" },
    { step: "MEMORY", phase: "database" },
    { step: "PROFILE", phase: "database" },
    { step: "PREFERENCES", phase: "database" },
    // Saved items point at trips and itineraries, so they go first.
    { step: "SAVED", phase: "database" },
    { step: "TRIPS", phase: "database" },
    { step: "REQUESTS", phase: "database" },
    { step: "CONVERSATIONS", phase: "database" },
    { step: "NOTIFICATIONS", phase: "database" },
    { step: "EXPORTS", phase: "database" },
    { step: "ANALYTICS", phase: "database" },
    { step: "STREAM", phase: "provider" },
    { step: "PUSH", phase: "provider" },
    { step: "STORAGE", phase: "provider" },
    { step: "AUTH", phase: "provider" },
    // Last of the provider steps: it waits for the buffers of the API to drain after SUSPEND, so the others run first (CHT-2030, ADR 0017).
    { step: "ANALYTICS_VENDOR", phase: "provider" },
    { step: "TOMBSTONE", phase: "database" },
]);
export const privacyDeleteStepCodes = Object.freeze(privacyDeleteSteps.map((entry) => entry.step));
export const privacyDatabaseStepCodes = Object.freeze(privacyDeleteSteps.filter((entry) => entry.phase === "database").map((entry) => entry.step));
export const privacyProviderStepCodes = Object.freeze(privacyDeleteSteps.filter((entry) => entry.phase === "provider").map((entry) => entry.step));
/** An EXPORT request has one step. */
export const privacyExportSteps = Object.freeze(["ASSEMBLE"]);
/** Every step code, written out so the database CHECK can be built from it. A test fails when this differs from the two lists above. */
export const privacyStepCodes = Object.freeze([
    "SUSPEND",
    "MEMORY",
    "PROFILE",
    "PREFERENCES",
    "SAVED",
    "TRIPS",
    "REQUESTS",
    "CONVERSATIONS",
    "NOTIFICATIONS",
    "EXPORTS",
    "ANALYTICS",
    "STREAM",
    "PUSH",
    "STORAGE",
    "AUTH",
    "ANALYTICS_VENDOR",
    "TOMBSTONE",
    "ASSEMBLE",
]);
/** The closed codes of a failed step. A provider message never reaches a row. */
export const privacyStepErrorCodes = Object.freeze([
    "PROVIDER_UNAVAILABLE",
    "PROVIDER_TIMEOUT",
    "PROVIDER_REFUSED",
    "PROVIDER_NOT_CONFIGURED",
    "DATABASE_ERROR",
    "EXPORT_TOO_LARGE",
    "INTERNAL",
]);
// --- limits -----------------------------------------------------------------------------------------------------------------------------
/** An export is one JSON document in Postgres. Larger than this, the job fails with `EXPORT_TOO_LARGE` and nothing is stored. */
export const PRIVACY_EXPORT_MAX_BYTES = 5 * 1024 * 1024;
/** A download stops working this long after the export was made. The retention task then removes the bytes. */
export const PRIVACY_EXPORT_TTL_MS = 24 * 60 * 60 * 1000;
/** A customer may ask for at most this many exports in 24 hours, counting those that failed. */
export const PRIVACY_EXPORT_DAILY_LIMIT = 3;
/** Downloads of one export. A download is audited every time. */
export const PRIVACY_EXPORT_MAX_DOWNLOADS = 5;
export const PRIVACY_EXPORT_FORMAT = "lafayette.customer-export.v1";
/**
 * What a deletion does, class by class. The policy is India-first and is NOT a legal opinion: every retention period marked `legalOpen`
 * is an assumption that the owner confirms with counsel before launch. The rule behind it: remove or anonymize everything that identifies the
 * person or holds what the person wrote, keep opaque ids and amounts where a law, a payment dispute or the integrity of the audit trail needs them.
 */
export const privacyRetentionMatrix = Object.freeze([
    {
        dataClass: "MEMORY_FACTS",
        label: "Remembered facts and preferences",
        holder: "customer_memory_facts",
        action: "ANONYMIZED",
        step: "MEMORY",
        why: "Every fact becomes a tombstone: the value is cleared and the status is DELETED. Retrieval never reads it. The row keeps its ids, kind and times so the history of the change stays whole.",
    },
    {
        dataClass: "MEMORY_CONTROLS",
        label: "Memory switches (pause, disabled kinds)",
        holder: "customer_memory_controls",
        action: "DELETED",
        step: "PREFERENCES",
        why: "A choice of the customer, with no duty to keep it. The customer no longer exists for memory.",
    },
    {
        dataClass: "MEMORY_EVIDENCE",
        label: "Evidence ids behind a fact",
        holder: "customer_memory_fact_evidence, memory_extraction_runs",
        action: "KEPT",
        step: null,
        why: "Holds ids only (no message text, no value). It keeps the history of a tombstone whole and links to nothing the customer wrote.",
        retention: "Kept as opaque ids for as long as the tombstone row.",
    },
    {
        dataClass: "PROFILE",
        label: "Name, e-mail, phone, locale, home market, avatar path",
        holder: "customer_profiles",
        action: "ANONYMIZED",
        step: "PROFILE",
        why: "Every personal column is cleared. The row stays so no other row loses its anchor.",
    },
    {
        dataClass: "PREFERENCES",
        label: "Notification and push settings",
        holder: "customer_notification_preferences, customer_push_settings",
        action: "DELETED",
        step: "PREFERENCES",
        why: "A choice of the customer, with no duty to keep it.",
    },
    {
        dataClass: "ANALYTICS_CHOICE",
        label: "Product analytics choice",
        holder: "customer_analytics_preferences",
        action: "ANONYMIZED",
        step: "ANALYTICS",
        why: "The row is set to opted out and stays, so nothing is ever sent again for this customer id. It holds no content.",
    },
    {
        dataClass: "ANALYTICS_VISITS",
        label: "App visits and screen totals (the derived analytics read model)",
        holder: "customer_app_visits, customer_app_visit_screens",
        action: "DELETED",
        step: "ANALYTICS",
        why: "Summaries of each foreground visit of the phone app and the views and seconds of each screen. No event is stored here. The rows are removed with the customer, and a daily job removes any visit 13 months after it started.",
        retention: "13 months from the start of the visit, then removed by the daily retention job.",
    },
    {
        dataClass: "TRIPS",
        label: "Trips, itineraries, items, notes and place evidence",
        holder: "trips, trip_destinations, itineraries, itinerary_sections, itinerary_items, itinerary_* evidence tables",
        action: "DELETED",
        step: "TRIPS",
        why: "Planning data written by or for the customer. No law requires Lafayette to keep it. Places and dates show where a person travels, so the rows are removed.",
    },
    {
        dataClass: "SAVED_ITEMS",
        label: "Saved items",
        holder: "saved_items",
        action: "DELETED",
        step: "SAVED",
        why: "A choice of the customer, with no duty to keep it.",
    },
    {
        dataClass: "REQUESTS",
        label: "Request text, answers and customer-visible updates",
        holder: "concierge_requests, concierge_request_questions, concierge_request_customer_updates",
        action: "ANONYMIZED",
        step: "REQUESTS",
        why: "Free text is replaced. The state, the times and the links to a payment stay, because a payment or a supplier booking may still refer to the request.",
    },
    {
        dataClass: "REQUEST_NOTES",
        label: "Staff internal notes and fulfilment evidence about the customer",
        holder: "concierge_request_internal_notes, concierge_request_fulfilment_evidence",
        action: "ANONYMIZED",
        step: "REQUESTS",
        why: "Staff wrote about a person. The text is replaced; the author, the time and the row stay for the audit trail.",
    },
    {
        dataClass: "REQUEST_FILES",
        label: "Files a request holds",
        holder: "concierge_request_attachments and the private storage bucket",
        action: "PROVIDER_DELETED",
        step: "STORAGE",
        why: "The file bytes are removed from storage. The row keeps a placeholder name and no path.",
    },
    {
        dataClass: "CONVERSATIONS",
        label: "Conversation records and handoff summaries",
        holder: "conversations, handoffs, conversation_message_refs",
        action: "ANONYMIZED",
        step: "CONVERSATIONS",
        why: "Message ids are removed. A handoff summary text is replaced. The conversation row stays with its times.",
    },
    {
        dataClass: "MESSAGES",
        label: "Chat messages and the chat user",
        holder: "Stream (channel and user)",
        action: "PROVIDER_DELETED",
        step: "STREAM",
        why: "Lafayette stores no message text. The channel and the user are deleted at the provider, with their messages.",
    },
    {
        dataClass: "PUSH_IDENTITY",
        label: "Push identity and devices",
        holder: "notification_identities, notification_device_bindings",
        action: "ANONYMIZED",
        step: "PUSH",
        why: "The identity ends for good and every device is unbound. The rows stay with a random id, an install id and closed codes. Expo push keeps no record of a person.",
    },
    {
        dataClass: "PUSH_TOKENS",
        label: "Expo push tokens of the devices",
        holder: "notification_push_tokens, notification_closed_sessions, notification_send_markers",
        action: "ANONYMIZED",
        step: "PUSH",
        why: "A token is held only while it is active, on the server only. It is erased when the device signs out, when the token is replaced or refused by Expo, and when the identity ends. An ended row keeps no token. A token is never exported and never logged. Two small tables hold no token and no person: a closed app session is one SHA-256 value and a time, deleted after two days; a send marker is the id of an outbox row and times.",
    },
    {
        dataClass: "NOTIFICATIONS",
        label: "Notification events and delivery records",
        holder: "notification_events, notification_deliveries",
        action: "ANONYMIZED",
        step: "NOTIFICATIONS",
        why: "Event content is cleared. Delivery rows hold ids and codes only and stay as proof of what was sent.",
    },
    {
        dataClass: "EXPORT_FILES",
        label: "Privacy export packages",
        holder: "privacy_export_artifacts",
        action: "DELETED",
        step: "EXPORTS",
        why: "A package holds the data that was just deleted. It is removed with the rest.",
    },
    {
        dataClass: "SIGN_IN_ACCOUNT",
        label: "Sign-in account",
        holder: "Supabase Auth and customers.auth_subject_id",
        action: "PROVIDER_DELETED",
        step: "AUTH",
        why: "The sign-in user is deleted at the provider, then the link in `customers` is replaced with a random id, so no token resolves to this customer again.",
    },
    {
        dataClass: "CUSTOMER_ROOT",
        label: "The customer row (an opaque id and a creation time)",
        holder: "customers",
        action: "KEPT",
        step: "TOMBSTONE",
        why: "Payment and audit rows point at it. After deletion it holds a random id, a creation time and an erased mark. It identifies nobody.",
        retention: "Kept as an opaque tombstone.",
    },
    {
        dataClass: "PAYMENTS",
        label: "Payment requests, quotes, refunds, provider calls and observations",
        holder: "payment_requests, payment_quotes, payment_refunds, payment_provider_calls, payment_observations, payment_request_transitions",
        action: "KEPT",
        step: null,
        why: "Financial records that a tax, accounting or dispute duty may require. They hold opaque ids, amounts, currency, provider references and the description of the thing that was paid for (a catalog item or an approved quote). The payment description, the quote text and the reason of a payment transition are KEPT as staff wrote them: staff must not put personal text there (OWNER ITEM). A deletion cuts only the link of a payment to a trip or an item. They hold no card data and no payment secret.",
        retention: "Assumed 8 years from the date of the payment, then removed by a later retention job.",
        legalOpen: true,
    },
    {
        dataClass: "AUDIT",
        label: "Audit trail",
        holder: "audit_events, command_receipts",
        action: "KEPT",
        step: null,
        why: "The audit trail proves who did what. By design it holds ids and closed codes, never a value that was deleted. One field is free text: the reason a staff member gave for a command (1 to 500 characters, for example for a handoff). It is KEPT as staff wrote it, and the deletion does not rewrite it: staff must not put personal text there (OWNER ITEM). The deletion adds its own rows with step codes and counts only.",
        retention: "Assumed 8 years, same as the payments they describe.",
        legalOpen: true,
    },
    {
        dataClass: "AGENT_RUNS",
        label: "Agent run records",
        holder: "agent_runs, conversation_replies",
        action: "KEPT",
        step: null,
        why: "Counts, codes, model names and cost. No prompt and no answer text.",
        retention: "Kept as opaque ids.",
    },
    {
        dataClass: "TELEMETRY",
        label: "Sentry, Mastra traces, logs",
        holder: "Sentry and the log store",
        action: "NOT_HELD",
        step: null,
        why: "By design they hold ids and closed codes only, never names, messages or memory values (docs/engineering/observability.md). Retention is set in the vendor project.",
    },
    {
        dataClass: "ANALYTICS_VENDOR",
        label: "Product analytics events at the vendor",
        holder: "PostHog",
        action: "PROVIDER_DELETED",
        step: "ANALYTICS_VENDOR",
        why: "Events carry a salted hash of the customer id, never a name or a message. Lafayette stops sending at once (the opt-out above). When a marker shows that events may exist at the vendor, the step waits 60 seconds for the send buffers, asks the vendor to delete the events of that hash, and is DONE only when a read shows none left. The real vendor deletion is NOT built: the vendor documents no deletion of events without a person profile, so with a marker the step fails closed until the owner decides (open item).",
        retention: "13 months at the vendor (an owner setting of the PostHog project).",
        legalOpen: true,
    },
]);
export const privacyRetentionActions = Object.freeze([
    "DELETED",
    "ANONYMIZED",
    "KEPT",
    "PROVIDER_DELETED",
    "NOT_HELD",
]);
// --- the test marker --------------------------------------------------------------------------------------------------------------------
/**
 * Privacy operations are proven only on SEEDED TEST customers. A test customer is tagged with a profile e-mail in this reserved domain
 * (the `.invalid` top level never resolves). The driver script under `scripts/acceptance/privacy/` refuses any other customer, and so do
 * the tests that seed one. A real member's e-mail never ends this way.
 */
export const PRIVACY_TEST_MARKER_DOMAIN = "privacy-test.lafayette.invalid";
export function isPrivacyTestEmail(email) {
    return (typeof email === "string" && email.toLowerCase().endsWith(`@${PRIVACY_TEST_MARKER_DOMAIN}`));
}
